import { EventBus } from '../core/Events';
import type { Settings } from '../core/Settings';

/**
 * Girdi katmanı.
 * Oyun kodu klavye/mouse/dokunma ayrımını bilmez; yalnızca soyut EYLEM durumunu okur.
 * Mobilde çoklu dokunma zorunlu: sol parmak hareket ederken sağ parmak kamerayı döndürebilir
 * ve aynı anda saldırı/zıplama butonlarına basılabilir.
 */

export type ActionName =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'sprint'
  | 'crouch'
  | 'jump'
  | 'attack'
  | 'interact'
  | 'power'
  | 'inventory'
  | 'map'
  | 'menu';

type InputEvents = {
  /** Basma anı (tek seferlik tetikleyiciler için). */
  pressed: { action: ActionName };
  released: { action: ActionName };
  /** Hotbar seçimi (1-8 tuşları veya tekerlek). */
  hotbar: { index: number };
  wheel: { delta: number };
};

export interface MoveVector {
  x: number;
  y: number;
}

const KEY_MAP: Record<string, ActionName> = {
  KeyW: 'forward',
  ArrowUp: 'forward',
  KeyS: 'back',
  ArrowDown: 'back',
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  ShiftLeft: 'sprint',
  ShiftRight: 'sprint',
  KeyC: 'crouch',
  Space: 'jump',
  KeyE: 'interact',
  KeyQ: 'power',
  KeyI: 'inventory',
  Tab: 'inventory',
  KeyM: 'map',
  Escape: 'menu',
};

export class InputManager {
  readonly events = new EventBus<InputEvents>();
  private state = new Set<ActionName>();
  /** Dokunmatik butonlardan gelen durum (klavyeden ayrı tutulur ki biri diğerini silmesin). */
  private touchState = new Set<ActionName>();
  /** Sol joystick vektörü (-1..1). */
  private stick: MoveVector = { x: 0, y: 0 };
  /** Bu karede biriken bakış deltası. */
  private lookDX = 0;
  private lookDY = 0;
  private pointerLocked = false;
  private enabled = true;
  private disposers: Array<() => void> = [];

  constructor(private canvas: HTMLCanvasElement, private settings: Settings) {
    this.bindKeyboard();
    this.bindMouse();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.state.clear();
      this.stick.x = 0;
      this.stick.y = 0;
      this.lookDX = 0;
      this.lookDY = 0;
    }
  }

  isDown(action: ActionName): boolean {
    return this.state.has(action) || this.touchState.has(action);
  }

  /** Hareket vektörü: klavye ve joystick birleşimi. */
  moveVector(): MoveVector {
    let x = this.stick.x;
    let y = this.stick.y;
    if (this.isDown('forward')) y += 1;
    if (this.isDown('back')) y -= 1;
    if (this.isDown('right')) x += 1;
    if (this.isDown('left')) x -= 1;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    return { x, y };
  }

  /** Kamera deltasını tüketir (okunduktan sonra sıfırlanır). */
  consumeLook(): { dx: number; dy: number } {
    const out = { dx: this.lookDX, dy: this.lookDY };
    this.lookDX = 0;
    this.lookDY = 0;
    return out;
  }

  get isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  requestPointerLock(): void {
    if (this.settings.isTouch) return;
    if (!this.pointerLocked && this.canvas.requestPointerLock) {
      void this.canvas.requestPointerLock();
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  // ---------------------------------------------------------------- dokunmatik API
  /** TouchControls bu metotları çağırır. */
  setTouchAction(action: ActionName, down: boolean): void {
    const had = this.touchState.has(action);
    if (down && !had) {
      this.touchState.add(action);
      this.events.emit('pressed', { action });
    } else if (!down && had) {
      this.touchState.delete(action);
      this.events.emit('released', { action });
    }
  }

  setStick(x: number, y: number): void {
    this.stick.x = clamp(x, -1, 1);
    this.stick.y = clamp(y, -1, 1);
  }

  addLook(dx: number, dy: number, touch: boolean): void {
    const s = touch ? this.settings.controls.touchSensitivity : this.settings.controls.cameraSensitivity;
    const inv = this.settings.controls.invertY ? -1 : 1;
    this.lookDX += dx * s;
    this.lookDY += dy * s * inv;
  }

  selectHotbar(index: number): void {
    this.events.emit('hotbar', { index });
  }

  // ---------------------------------------------------------------- bağlamalar
  private bindKeyboard(): void {
    const down = (e: KeyboardEvent) => {
      if (!this.enabled) return;
      // Metin girişi varken oyun kısayolları çalışmasın
      if (isTextTarget(e.target)) return;
      const action = KEY_MAP[e.code];
      if (e.code.startsWith('Digit')) {
        const n = Number(e.code.slice(5));
        if (n >= 1 && n <= 8) {
          e.preventDefault();
          this.selectHotbar(n - 1);
          return;
        }
      }
      if (!action) return;
      if (e.code === 'Space' || e.code === 'Tab') e.preventDefault();
      if (e.repeat) return;
      this.state.add(action);
      this.events.emit('pressed', { action });
    };
    const up = (e: KeyboardEvent) => {
      const action = KEY_MAP[e.code];
      if (!action) return;
      if (this.state.delete(action)) this.events.emit('released', { action });
    };
    // Sekme değişince tuşlar basılı kalmasın
    const blur = () => {
      for (const a of [...this.state]) {
        this.state.delete(a);
        this.events.emit('released', { action: a });
      }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    this.disposers.push(() => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    });
  }

  private bindMouse(): void {
    /**
     * Piksel başına dönüş miktarı (radyan).
     * 1920 piksellik tam ekran süpürme ≈ 120°, standart FPS hissi.
     * Kullanıcı bunu ayarlardaki "Kamera hassasiyeti" ile 0.2x–3x ölçekler.
     */
    const RAD_PER_PX = 0.0011;
    /** Tek olayda gelebilecek en büyük hareket: sürücü/DPI kaynaklı sıçramalar ekranı savurmasın. */
    const MAX_STEP_PX = 180;
    const step = (v: number) => Math.max(-MAX_STEP_PX, Math.min(MAX_STEP_PX, v)) * RAD_PER_PX;

    const move = (e: MouseEvent) => {
      if (!this.enabled) return;
      if (!this.pointerLocked && !this.dragging) return;
      this.addLook(-step(e.movementX), -step(e.movementY), false);
    };
    const down = (e: MouseEvent) => {
      if (!this.enabled) return;
      if (e.button === 0) {
        if (!this.pointerLocked && !this.settings.isTouch) {
          this.requestPointerLock();
        }
        this.dragging = true;
        this.state.add('attack');
        this.events.emit('pressed', { action: 'attack' });
      } else if (e.button === 2) {
        this.state.add('interact');
        this.events.emit('pressed', { action: 'interact' });
      }
    };
    const up = (e: MouseEvent) => {
      if (e.button === 0) {
        this.dragging = false;
        if (this.state.delete('attack')) this.events.emit('released', { action: 'attack' });
      } else if (e.button === 2) {
        if (this.state.delete('interact')) this.events.emit('released', { action: 'interact' });
      }
    };
    const wheel = (e: WheelEvent) => {
      if (!this.enabled || isTextTarget(e.target)) return;
      this.events.emit('wheel', { delta: Math.sign(e.deltaY) });
    };
    const ctx = (e: Event) => e.preventDefault();
    const lockChange = () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    };

    this.canvas.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('wheel', wheel, { passive: true });
    this.canvas.addEventListener('contextmenu', ctx);
    document.addEventListener('pointerlockchange', lockChange);
    this.disposers.push(() => {
      this.canvas.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('wheel', wheel);
      this.canvas.removeEventListener('contextmenu', ctx);
      document.removeEventListener('pointerlockchange', lockChange);
    });
  }

  private dragging = false;

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.state.clear();
    this.touchState.clear();
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function isTextTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable === true;
}
